import { Spinner, Text } from "@hackthenorth/north";
import React from "react";
import styled, { css } from "styled-components";

import {
  Divider,
  Header,
  InfoTag,
  Link,
  PageWrapper,
} from "src/shared/components";
import { useEventContext } from "src/shared/contexts";
import { ExternalLink } from "src/static/icons";
import { mediaQueries } from "src/theme/deviceBreakpoints";

import { DevpostCountdown } from "./components/DevpostCountdown";
import { JudgingDetailsCard } from "./components/JudgingDetailsCard";
import {
  DEVPOST_SYNC_WARNING_BANNER,
  getJudgingScheduleSteps,
  getJudgingStatusBanner,
  JUDGING_BANNER_ICON,
  JUDGING_DEVPOST_URL,
  JudgingBannerVariant,
  NO_EVENT_ATTENDEE_BANNER,
} from "./constants";
import { useGetMyJudgingInfoQuery } from "./graphql/getMyJudgingInfo.generated";
import { isMissingHackerEventAttendee } from "./missingEventAttendee";

export const JudgingInfo: React.FC = () => {
  const { judging } = useEventContext();

  const { data: judgingData, loading: isJudgingInfoLoading } =
    useGetMyJudgingInfoQuery();

  const judgingInfo = judgingData?.myJudgingInfo;
  const badgeId = judgingInfo?.badgeCode;
  const hasNoEventAttendee = isMissingHackerEventAttendee(
    judgingInfo,
    !isJudgingInfoLoading && Boolean(judgingData)
  );
  const banner = hasNoEventAttendee
    ? NO_EVENT_ATTENDEE_BANNER
    : getJudgingStatusBanner(
        judgingInfo?.status,
        new Date(),
        judging.draftDeadline,
        judging.finalDeadline
      );
  const BannerIcon = JUDGING_BANNER_ICON[banner.variant];
  const SyncWarningIcon =
    JUDGING_BANNER_ICON[DEVPOST_SYNC_WARNING_BANNER.variant];
  const isProjectSubmitted = Boolean(judgingInfo?.projectTitle);
  const scheduleItems = getJudgingScheduleSteps(
    judging.R1StartTime,
    judging.R2StartTime,
    judging.draftDeadline,
    judging.finalDeadline
  );

  return (
    <>
      <Header
        title="Judging Info"
        subtitle="Track your project submission and judging status throughout the weekend."
      />
      <PageWrapper fullWidth>
        <Content>
          <StatusBanner
            role="status"
            $variant={DEVPOST_SYNC_WARNING_BANNER.variant}
          >
            <StatusIconFrame>
              <SyncWarningIcon aria-hidden />
            </StatusIconFrame>
            <div>
              <BannerTitle $variant={DEVPOST_SYNC_WARNING_BANNER.variant}>
                {DEVPOST_SYNC_WARNING_BANNER.title}
              </BannerTitle>
              <BannerText $variant={DEVPOST_SYNC_WARNING_BANNER.variant}>
                {DEVPOST_SYNC_WARNING_BANNER.description}
              </BannerText>
            </div>
          </StatusBanner>

          <ScheduleSection>
            <ScheduleTitle mods="heading h3">How Judging Works</ScheduleTitle>
            <ScheduleItems>
              {scheduleItems.map((step) => (
                <ScheduleItem key={step.id}>
                  <StepIcon>{step.icon}</StepIcon>
                  <StepTitle>{step.title}</StepTitle>
                  <StepTime>{step.time}</StepTime>
                  <StepDescription>{step.description}</StepDescription>
                </ScheduleItem>
              ))}
            </ScheduleItems>
            <Divider />
          </ScheduleSection>

          {isJudgingInfoLoading ? (
            <Spinner />
          ) : (
            <MainGrid>
              <StatusColumn>
                <StatusBanner role="status" $variant={banner.variant}>
                  <StatusIconFrame>
                    <BannerIcon aria-hidden />
                  </StatusIconFrame>
                  <div>
                    <BannerTitle $variant={banner.variant}>
                      {banner.title}
                    </BannerTitle>
                    <BannerText $variant={banner.variant}>
                      {banner.description}
                    </BannerText>
                  </div>
                </StatusBanner>
                {banner.showDevpostCta && (
                  <DevpostButton href={JUDGING_DEVPOST_URL} newTab>
                    Go to Devpost
                    <ExternalLink aria-hidden />
                  </DevpostButton>
                )}
                <JudgingDetailsCard
                  status={judgingInfo?.status}
                  judgingLocation={judgingInfo?.judgingLocation}
                  judgingTime={judgingInfo?.judgingTime}
                />
              </StatusColumn>

              <InfoColumn>
                <InfoCard>
                  <CardTitle>Your Info</CardTitle>
                  <InfoField>
                    <FieldLabel>Badge ID</FieldLabel>
                    <FieldValue>{badgeId ?? "—"}</FieldValue>
                  </InfoField>
                  <InfoField>
                    <FieldLabel>Team Phone Number</FieldLabel>
                    <FieldValue>{judgingInfo?.phoneNumber ?? "—"}</FieldValue>
                  </InfoField>
                  <InfoField>
                    <FieldLabel>Teammates</FieldLabel>
                    {judgingInfo?.teammates?.length ? (
                      judgingInfo.teammates.map((teammate) => (
                        <FieldValue key={teammate}>{teammate}</FieldValue>
                      ))
                    ) : (
                      <FieldValue>—</FieldValue>
                    )}
                  </InfoField>
                  <InfoField>
                    <FieldLabel>Sponsor Tracks</FieldLabel>
                    {judgingInfo?.sponsorTracks?.length ? (
                      <SponsorTrackTags>
                        {judgingInfo.sponsorTracks.map((track) => (
                          <InfoTag key={track}>{track}</InfoTag>
                        ))}
                      </SponsorTrackTags>
                    ) : (
                      <FieldValue>—</FieldValue>
                    )}
                  </InfoField>
                </InfoCard>

                <InfoCard>
                  <CardTitle>Devpost Submission</CardTitle>
                  {isProjectSubmitted ? (
                    judgingInfo?.devpostLink ? (
                      <ProjectLink href={judgingInfo.devpostLink} newTab>
                        {judgingInfo.projectTitle}
                      </ProjectLink>
                    ) : (
                      <FieldValue>{judgingInfo?.projectTitle}</FieldValue>
                    )
                  ) : (
                    <StatusPill $variant={JudgingBannerVariant.Danger}>
                      Not received
                    </StatusPill>
                  )}
                  <DevpostCountdown status={judgingInfo?.status} />
                </InfoCard>
              </InfoColumn>
            </MainGrid>
          )}
        </Content>
      </PageWrapper>
    </>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const ScheduleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;

  ${mediaQueries.tablet} {
    order: 1;
  }
`;

const ScheduleTitle = styled(Text)`
  text-align: left;
`;

const ScheduleItems = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
  flex-wrap: wrap;

  ${mediaQueries.tablet} {
    flex-direction: column;
  }
`;

const ScheduleItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 180px;
  min-width: 180px;

  ${mediaQueries.tablet} {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
  }
`;

const StepIcon = styled.div`
  display: flex;
  width: 24px;
  height: 24px;
  margin-bottom: 4px;
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};

  svg {
    width: 100%;
    height: 100%;
    fill: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  }
`;

const StepTitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 700;
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
`;

const StepTime = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};

  a {
    font: inherit;
    font-weight: inherit;
    color: inherit;
  }
`;

const StepDescription = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const MainGrid = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 36px;
  width: 100%;

  ${mediaQueries.tablet} {
    flex-direction: column;
    order: 0;
  }
`;

const StatusColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 1 0;
  min-width: 0;
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 0 0 350px;
  width: 350px;

  ${mediaQueries.tablet} {
    width: 100%;
    flex: 1 1 auto;
  }
`;

const InfoCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.globalConstants.color.white};
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
`;

const CardTitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 700;
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
`;

const InfoField = styled.div`
  display: flex;
  flex-direction: column;
`;

const SponsorTrackTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 3px -3px 0;
`;

const FieldLabel = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 16px;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
`;

const FieldValue = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 700;
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const ProjectLink = styled(Link)`
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 700 !important;
  font-size: 16px !important;
  line-height: 1.6 !important;
`;

const DevpostButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderPrimary};
  border-radius: 4px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 700 !important;
  font-size: 16px !important;
  color: ${({ theme }) => theme.globalConstants.color.textLight} !important;
  text-decoration: none !important;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const variantTextColor = (variant: JudgingBannerVariant) => css`
  color: ${({ theme }) =>
    variant === JudgingBannerVariant.Danger
      ? theme.globalConstants.color.textDanger
      : variant === JudgingBannerVariant.Success
      ? theme.globalConstants.color.textSuccess
      : variant === JudgingBannerVariant.Warning
      ? theme.globalConstants.color.textWarning
      : theme.globalConstants.color.brandPrimary};
`;

const variantBackgroundColor = (variant: JudgingBannerVariant) => css`
  background: ${({ theme }) =>
    variant === JudgingBannerVariant.Danger
      ? theme.globalConstants.color.backgroundDangerLight
      : variant === JudgingBannerVariant.Success
      ? theme.globalConstants.color.greenSecondary
      : variant === JudgingBannerVariant.Warning
      ? theme.globalConstants.color.backgroundWarningLight
      : theme.globalConstants.color.backgroundPrimaryLight};
`;

const StatusPill = styled(InfoTag)<{ $variant: JudgingBannerVariant }>`
  align-self: flex-start;
  padding: 4px 9px;
  border-radius: 28px;
  font-weight: 700;
  font-size: 12px;
  ${({ $variant }) => variantBackgroundColor($variant)}
  ${({ $variant }) => variantTextColor($variant)}
`;

const StatusBanner = styled.div<{ $variant: JudgingBannerVariant }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  border-radius: 8px;
  ${({ $variant }) => variantBackgroundColor($variant)}
`;

const StatusIconFrame = styled.span`
  display: inline-flex;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  margin-top: 4px;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const BannerTitle = styled.p<{ $variant: JudgingBannerVariant }>`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-weight: 900;
  font-size: 20px;
  ${({ $variant }) => variantTextColor($variant)}
`;

const BannerText = styled.div<{ $variant: JudgingBannerVariant }>`
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5;
  ${({ $variant }) => variantTextColor($variant)}

  p {
    margin: 0 0 16px;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;
